import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, ArrowRight, Sparkles, Crown, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import React, { useEffect, useState } from 'react';
export default function Success() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscription, loading, refreshSubscription ,getCurrentPlan} = useSubscription();
  const sessionId = searchParams.get('session_id');
  const { t, i18n } = useTranslation();
  const { isRTL, language } = useTheme();

  // Helper function to get translated field values
  const getTranslatedField = (fallback, translationsObj) => {
    if (!translationsObj || typeof translationsObj !== 'object') {
      return fallback;
    }
    return translationsObj[language] || translationsObj.en || fallback;
  };

  const subscriptionDetails = subscription?.subscription;
  const currentPlanName = getTranslatedField(
    subscriptionDetails?.plan_name,
    subscriptionDetails?.plan_translations || subscriptionDetails?.plan_name_translations
  );

  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // Check if the provider redirected here with a failure status (e.g., Paymob, Stripe, PayPal)
    const isSuccess = searchParams.get('success');
    const status = searchParams.get('status');
    const redirectStatus = searchParams.get('redirect_status');
    
    if (
      isSuccess === 'false' || 
      status === 'failed' || 
      redirectStatus === 'failed' || 
      searchParams.get('canceled') === 'true'
    ) {
      navigate('/failed' + window.location.search, { replace: true });
      return;
    }

    // Refresh subscription data once on mount to get the latest status after payment
    refreshSubscription();
  }, [searchParams, navigate]);

  // Poll refreshSubscription if subscription is still free/inactive due to webhook delay
  useEffect(() => {
    if (subscription && !subscription.has_active_subscription && retryCount < 5) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        refreshSubscription();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [subscription, retryCount]);

  // Debug subscription data
  const currentPlan = getCurrentPlan();

  const handleContinue = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center px-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-slate-600">{t('paymentSuccess.loadingMessage', 'Processing your subscription...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" dir={isRTL ? 'rtl' : 'ltr'}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl w-full"
      >
        {/* Success Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header with animated checkmark */}
          <div className="relative bg-[#2F80ED] px-8 py-12 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 20,
                delay: 0.2
              }}
              className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full mb-4 shadow-lg"
            >
              <CheckCircle2 className="w-14 h-14 text-[#2F80ED]" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-4xl font-black text-white mb-2"
            >
              {t('paymentSuccess.title', 'Payment Successful!')}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-blue-50 text-lg"
            >
              {t('paymentSuccess.subtitle', 'Welcome to your upgraded experience')}
            </motion.p>

            {/* Decorative elements */}
            <div className="absolute top-4 left-4 text-white/20">
              <Sparkles className="w-8 h-8" />
            </div>
            <div className="absolute bottom-4 right-4 text-white/20">
              <Sparkles className="w-6 h-6" />
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-10">
            {subscriptionDetails ? (
              <>
                {/* Subscription Details */}
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                    <Crown className="w-6 h-6 text-yellow-500" />
                    {t('paymentSuccess.subscriptionDetails', 'Your Subscription Details')}
                  </h2>

                  <div className="space-y-4">
                    {/* Plan Name */}
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                          <Zap className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-600 font-medium">{t('paymentSuccess.planLabel', 'Plan')}</p>
                          <p className="text-xl font-bold text-slate-900">
                            {subscriptionDetails ? currentPlan : 'Loading plan details...'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-black text-blue-600">
                          ${subscriptionDetails?.plan_price}
                        </p>
                        <p className="text-sm text-slate-500">{subscriptionDetails?.plan_currency}/month</p>
                      </div>
                    </div>

                    {/* Trial Info */}
                    {subscription.is_trial && subscription.days_remaining_in_trial && (
                      <div className="p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
                        <div className="flex items-start gap-3">
                          <Sparkles className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-yellow-900">{t('paymentSuccess.trialTitle', 'Trial Period Active')}</p>
                            <p className="text-sm text-yellow-700">
                              {t('paymentSuccess.trialRemaining', 'You have {{days}} days remaining in your free trial', {
                                days: subscription.days_remaining_in_trial
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Billing Period */}
                    {subscriptionDetails?.current_period_end && (
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center justify-between">
                          <p className="text-slate-600">{t('paymentSuccess.nextBillingDate', 'Next billing date')}</p>
                          <p className="font-semibold text-slate-900">
                            {new Date(subscriptionDetails.current_period_end).toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Usage Limits */}
                    <div className="grid grid-cols-2 gap-4">
                      {subscriptionDetails?.max_cv_generations && (
                        <div className="p-4 bg-blue-50 rounded-xl text-center">
                          <p className="text-sm text-slate-600 mb-1">{t('paymentSuccess.cvGenerations', 'CV Generations')}</p>
                          <p className="text-3xl font-bold text-[#2F80ED]">
                            {subscriptionDetails.max_cv_generations === -1
                              ? '∞'
                              : subscriptionDetails.max_cv_generations}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">{t('paymentSuccess.perMonth', 'per month')}</p>
                        </div>
                      )}
                      {subscriptionDetails?.max_job_applications && (
                        <div className="p-4 bg-blue-50 rounded-xl text-center">
                          <p className="text-sm text-slate-600 mb-1">{t('paymentSuccess.jobApplications', 'Job Applications')}</p>
                          <p className="text-3xl font-bold text-[#2F80ED]">
                            {subscriptionDetails.max_job_applications === -1
                              ? '∞'
                              : subscriptionDetails.max_job_applications}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">{t('paymentSuccess.perMonth', 'per month')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Confirmation Message */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
                  <p className="text-blue-800 text-center">
                    <span className="font-semibold">{t('paymentSuccess.confirmationTitle', '🎉 Confirmation email sent!')}</span>
                    <br />
                    <span className="text-sm">{t('paymentSuccess.confirmationSubtitle', 'Check your inbox for the receipt and subscription details.')}</span>
                  </p>
                </div>
              </>
            ) : (
              <div className="text-center mb-8">
                <p className="text-slate-700 text-lg mb-2">
                  {t('paymentSuccess.thankYouTitle', 'Thank you for your payment!')}
                </p>
                <p className="text-slate-600">
                  {t('paymentSuccess.thankYouSubtitle', 'Your subscription has been activated successfully.')}
                </p>
              </div>
            )}

            {/* Action Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleContinue}
              className="w-full bg-[#2F80ED] hover:bg-[#1E6FCC] text-white font-bold py-4 px-8 rounded-xl flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {t('paymentSuccess.continueButton', 'Continue to Dashboard')}
              <ArrowRight className="w-5 h-5" />
            </motion.button>

            {/* Session ID (for debugging) */}
            {sessionId && (
              <p className="text-xs text-slate-400 text-center mt-6">
                {t('paymentSuccess.sessionId', 'Session ID')}: {sessionId}
              </p>
            )}
          </div>
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-8 text-slate-600 text-sm"
        >
          <p>
            {t('paymentSuccess.supportPrompt', 'Need help? Contact our support team at')}{' '}
            <a href="mailto:support@cvlogs.com" className="text-blue-600 hover:underline font-medium">
              {t('paymentSuccess.supportEmail', 'support@cvlogs.com')}
            </a>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
